import { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import * as LucideIcons from 'lucide-react';

// Destructure commonly used icons for the modal UI
const { X, Search, Lightbulb, Wrench, MessageCircle, Mail, Phone, Plus, Edit2 } = LucideIcons;

export const AddSkillModal = ({
    show,
    onClose,
    onSubmit,
    newSkillData,
    setNewSkillData,
    allCategories,
    customSkillCategory,
    setCustomSkillCategory,
    isEditMode = false,
}) => {
    const [categorySearch, setCategorySearch] = useState('');
    const [iconSearch, setIconSearch] = useState('');

    // Curated selection of useful icons (emoji-like)
    const AVAILABLE_ICONS = useMemo(() => {
        const iconNames = [
            // People & Body
            'User', 'Users', 'UserCheck', 'UserPlus', 'Heart', 'Smile', 'Frown', 'Meh',
            // Nature & Places
            'Home', 'Building', 'Building2', 'Store', 'TreeDeciduous', 'TreePine', 'Flower', 'Leaf',
            'Sun', 'Moon', 'Star', 'Cloud', 'CloudRain', 'Umbrella', 'Zap', 'Flame',
            // Activities & Hobbies
            'Camera', 'Music', 'Music2', 'Music3', 'Music4', 'Mic', 'Headphones', 'Radio',
            'Palette', 'Paintbrush', 'Pencil', 'Pen', 'Scissors', 'Book', 'BookOpen', 'Newspaper',
            'Trophy', 'Award', 'Medal', 'Target', 'Dumbbell', 'Bike', 'Car', 'Plane',
            // Objects & Tools
            'Hammer', 'Drill', 'Screwdriver', 'Ruler', 'Briefcase', 'ShoppingBag', 'ShoppingCart',
            'Gift', 'Package', 'Shirt', 'Glasses', 'Watch', 'Key', 'Lock', 'Unlock',
            // Food & Drink
            'Coffee', 'Wine', 'Beer', 'Pizza', 'Cake', 'Apple', 'Cherry', 'Croissant',
            // Tech & Work
            'Laptop', 'Monitor', 'Smartphone', 'Tablet', 'Wifi', 'Code', 'Terminal', 'Bug',
            'Database', 'Server', 'HardDrive', 'Cpu', 'Battery', 'Plug', 'Bluetooth',
            // Education & Learning
            'GraduationCap', 'School', 'Microscope', 'Telescope', 'Calculator', 'Globe',
            // Business & Finance
            'DollarSign', 'CreditCard', 'Banknote', 'PiggyBank', 'TrendingUp', 'TrendingDown',
            'BarChart', 'PieChart', 'LineChart',
            // Communication
            'MessageSquare', 'MessagesSquare', 'Send', 'Inbox', 'AtSign',
            // Creative & Design
            'Sparkles', 'Wand', 'Gem', 'Crown', 'Image', 'Video', 'Film', 'FileImage',
            // Health & Medical
            'Heart', 'HeartPulse', 'Pill', 'Stethoscope', 'Syringe', 'Thermometer',
            // Nature & Animals
            'Dog', 'Cat', 'Bird', 'Fish', 'Bug', 'Squirrel',
            // Symbols & Signs
            'CheckCircle', 'XCircle', 'AlertCircle', 'Info', 'HelpCircle', 'AlertTriangle',
            'ThumbsUp', 'ThumbsDown', 'Flag', 'Bookmark', 'Bell', 'Calendar',
            // Arrows & Direction
            'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'MoveUp', 'MoveDown',
            // Misc Useful
            'Sparkle', 'Gamepad2', 'Puzzle', 'Rocket', 'Compass', 'Map', 'MapPin',
            'Anchor', 'Shield', 'Footprints', 'Fingerprint', 'Eye', 'Ear', 'Hand'
        ];

        const icons = [];
        iconNames.forEach(iconName => {
            const IconComponent = LucideIcons[iconName];
            if (IconComponent) {
                icons.push({
                    name: iconName,
                    icon: IconComponent,
                    label: iconName.replace(/([A-Z])/g, ' $1').trim()
                });
            }
        });

        return icons;
    }, []);

    if (!show) return null;

    // Filter categories based on search
    const filteredCategories = allCategories.filter(cat =>
        cat.toLowerCase().includes(categorySearch.toLowerCase())
    );

    // Filter icons based on search
    const filteredIcons = (AVAILABLE_ICONS || []).filter(({ name }) =>
        !iconSearch || name.toLowerCase().includes(iconSearch.toLowerCase())
    );

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-[99999] p-4"
            onClick={onClose}
        >
            <motion.div
                initial={{ opacity: 0, scale: 0.9, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: 20 }}
                transition={{ type: "spring", bounce: 0.3, duration: 0.6 }}
                className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] flex flex-col overflow-hidden ring-1 ring-black/5"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="relative bg-gradient-to-r from-brand-500 via-brand-600 to-brand-700 p-6 text-white flex-shrink-0 overflow-hidden">
                    {/* Decorative elements */}
                    <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
                    <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full blur-2xl translate-y-1/2 -translate-x-1/2"></div>

                    <div className="relative flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <motion.div
                                initial={{ scale: 0, rotate: -180 }}
                                animate={{ scale: 1, rotate: 0 }}
                                transition={{ type: "spring", bounce: 0.5, duration: 0.8 }}
                                className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/30 shadow-lg"
                            >
                                {newSkillData.type === 'skill' ? (
                                    <Wrench className="w-6 h-6" strokeWidth={2.5} />
                                ) : (
                                    <Lightbulb className="w-6 h-6" strokeWidth={2.5} />
                                )}
                            </motion.div>
                            <motion.h3
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: 0.1 }}
                                className="text-2xl font-bold drop-shadow-sm"
                            >
                                {isEditMode
                                    ? `ערוך ${newSkillData.type === 'skill' ? 'כישורים' : 'ידע ותחומי עניין'}`
                                    : `הוסף ${newSkillData.type === 'skill' ? 'כישורים' : 'ידע ותחומי עניין'}`
                                }
                            </motion.h3>
                        </div>
                        <motion.button
                            onClick={onClose}
                            whileHover={{ scale: 1.1, rotate: 90 }}
                            whileTap={{ scale: 0.9 }}
                            className="text-white/80 hover:text-white transition-colors bg-white/10 hover:bg-white/20 p-2 rounded-lg backdrop-blur-sm"
                        >
                            <X size={24} strokeWidth={2.5} />
                        </motion.button>
                    </div>
                </div>

                {/* Scrollable Form Content */}
                <div className="p-6 space-y-5 overflow-y-auto flex-1">

                    {/* Title */}
                    <div>
                        <label className="block text-sm font-semibold text-ink-strong mb-2.5 flex items-center gap-2">
                            <div className="w-1.5 h-1.5 bg-gradient-to-r from-secondary-500 to-[#C87D3D] rounded-full"></div>
                            כותרת *
                        </label>
                        <input
                            type="text"
                            value={newSkillData.title}
                            onChange={(e) => setNewSkillData({ ...newSkillData, title: e.target.value })}
                            placeholder="לדוגמה: פיתוח ווב, גינון, צילום..."
                            className="w-full px-4 py-3 rounded-xl border-2 border-line focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 outline-none transition-all text-ink-strong placeholder:text-ink-subtle bg-white hover:border-line-strong"
                        />
                    </div>

                    {/* Description */}
                    <div>
                        <label className="block text-sm font-semibold text-ink-strong mb-2.5 flex items-center gap-2">
                            <div className="w-1.5 h-1.5 bg-gradient-to-r from-brand-500 to-brand-600 rounded-full"></div>
                            תיאור (אופציונלי)
                        </label>
                        <textarea
                            value={newSkillData.description}
                            onChange={(e) => setNewSkillData({ ...newSkillData, description: e.target.value })}
                            placeholder="ספר קצת יותר על כישורים/ידע שלך..."
                            rows={3}
                            className="w-full px-4 py-3 rounded-xl border-2 border-line focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 outline-none transition-all resize-none text-ink-strong placeholder:text-ink-subtle bg-white hover:border-line-strong"
                        />
                    </div>

                    {/* Icon Selection */}
                    <div>
                        <label className="block text-sm font-semibold text-ink-strong mb-2.5 flex items-center gap-2">
                            <div className="w-1.5 h-1.5 bg-gradient-to-r from-brand-400 to-brand-500 rounded-full"></div>
                            בחר אייקון
                        </label>

                        {/* Icon Search Input */}
                        <div className="relative mb-3">
                            <Search size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-subtle" strokeWidth={2.5} />
                            <input
                                type="text"
                                value={iconSearch}
                                onChange={(e) => setIconSearch(e.target.value)}
                                placeholder="חפש אייקון..."
                                className="w-full pr-10 pl-4 py-2.5 rounded-lg border-2 border-line focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 outline-none transition-all text-sm text-ink-strong placeholder:text-ink-subtle bg-white hover:border-line-strong"
                            />
                        </div>

                        <div className="relative">
                            <div className="grid grid-cols-6 gap-2 p-4 border-2 border-line rounded-xl bg-gradient-to-br from-surface-muted to-white max-h-64 overflow-y-auto scrollbar-thin scrollbar-thumb-neutral-300 scrollbar-track-neutral-100">
                                {filteredIcons.length > 0 ? (
                                    filteredIcons.map(({ name, icon: IconComponent }) => (
                                    <motion.button
                                        key={name}
                                        type="button"
                                        onClick={() => setNewSkillData({ ...newSkillData, icon: name })}
                                        whileHover={{ scale: 1.15, y: -2 }}
                                        whileTap={{ scale: 0.9 }}
                                        className={`p-3 rounded-lg transition-all relative group ${newSkillData.icon === name
                                            ? 'bg-gradient-to-br from-brand-500 via-brand-600 to-brand-700 text-white shadow-lg'
                                            : 'bg-white text-ink-muted hover:bg-gradient-to-br hover:from-brand-300/20 hover:to-brand-400/10 border border-line hover:border-brand-400'
                                            }`}
                                        title={name}
                                    >
                                        {newSkillData.icon === name && (
                                            <motion.div
                                                layoutId="selectedIcon"
                                                className="absolute inset-0 bg-gradient-to-br from-brand-500 to-brand-700 rounded-lg"
                                                transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                                            />
                                        )}
                                        <IconComponent className={`w-5 h-5 mx-auto relative z-10 ${newSkillData.icon === name ? 'drop-shadow-sm' : ''}`} strokeWidth={2} />
                                    </motion.button>
                                    ))
                                ) : (
                                    <div className="col-span-6 text-center py-8 text-ink-subtle text-sm">
                                        לא נמצאו אייקונים
                                    </div>
                                )}
                            </div>
                            {filteredIcons.length > 0 && (
                                <div className="mt-2 text-xs text-ink-muted text-center">
                                    {filteredIcons.length} אייקונים זמינים
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Contact Details */}
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="relative overflow-hidden bg-gradient-to-br from-warning-50 via-warning-50/50 to-warning-50 border-2 border-warning-100/60 rounded-xl p-5 space-y-4 shadow-sm"
                    >
                        {/* Decorative gradient overlay */}
                        <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-warning-100/20 to-transparent rounded-full blur-2xl"></div>

                        <div className="relative">
                            <label className="block text-sm font-bold text-ink-strong flex items-center gap-2 mb-1">
                                <Phone className="w-4 h-4 text-warning-600" strokeWidth={2.5} />
                                פרטי קשר *
                            </label>
                            <p className="text-xs text-ink-muted leading-relaxed">יש לספק לפחות אמצעי קשר אחד כדי שאנשים יוכלו ליצור איתך קשר</p>
                        </div>

                        {/* Phone Number */}
                        <div className="relative">
                            <label className="block text-xs font-semibold text-ink-muted mb-2 flex items-center gap-1.5">
                                <div className="w-1 h-1 bg-warning-500 rounded-full"></div>
                                טלפון
                            </label>
                            <div className="space-y-2.5">
                                <input
                                    type="tel"
                                    value={newSkillData.phone || ''}
                                    onChange={(e) => setNewSkillData({ ...newSkillData, phone: e.target.value })}
                                    placeholder="05X-XXXXXXX"
                                    className="w-full px-4 py-2.5 rounded-lg border-2 border-warning-100/60 bg-white focus:border-warning-500 focus:ring-2 focus:ring-warning-100/30 outline-none transition-all text-sm text-ink-strong placeholder:text-ink-subtle hover:border-warning-100"
                                />
                                {newSkillData.phone && (
                                    <motion.label
                                        initial={{ opacity: 0, x: -10 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        className="flex items-center gap-2.5 text-sm text-ink-strong cursor-pointer bg-white/60 p-2.5 rounded-lg hover:bg-white/80 transition-all"
                                    >
                                        <input
                                            type="checkbox"
                                            checked={newSkillData.hasWhatsApp || false}
                                            onChange={(e) => setNewSkillData({ ...newSkillData, hasWhatsApp: e.target.checked })}
                                            className="w-4 h-4 accent-success-500 rounded"
                                        />
                                        <span className="flex items-center gap-1.5">
                                            <MessageCircle className="w-3.5 h-3.5 text-success-600" strokeWidth={2.5} />
                                            זמין גם בוואטסאפ
                                        </span>
                                    </motion.label>
                                )}
                            </div>
                        </div>

                        {/* Email */}
                        <div className="relative">
                            <label className="block text-xs font-semibold text-ink-muted mb-2 flex items-center gap-1.5">
                                <div className="w-1 h-1 bg-warning-500 rounded-full"></div>
                                אימייל
                            </label>
                            <div className="relative">
                                <Mail className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-subtle" strokeWidth={2} />
                                <input
                                    type="email"
                                    value={newSkillData.email || ''}
                                    onChange={(e) => setNewSkillData({ ...newSkillData, email: e.target.value })}
                                    placeholder="example@email.com"
                                    className="w-full pr-10 pl-4 py-2.5 rounded-lg border-2 border-warning-100/60 bg-white focus:border-warning-500 focus:ring-2 focus:ring-warning-100/30 outline-none transition-all text-sm text-ink-strong placeholder:text-ink-subtle hover:border-warning-100"
                                />
                            </div>
                        </div>
                    </motion.div>

                    {/* Categories */}
                    <div>
                        <label className="block text-sm font-semibold text-ink-strong mb-2.5 flex items-center gap-2">
                            <div className="w-1.5 h-1.5 bg-gradient-to-r from-secondary-500 to-[#B8956B] rounded-full"></div>
                            קטגוריות (עד 3)
                        </label>

                        {/* Search Input */}
                        <div className="relative mb-3">
                            <Search size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-subtle" strokeWidth={2.5} />
                            <input
                                type="text"
                                value={categorySearch}
                                onChange={(e) => setCategorySearch(e.target.value)}
                                placeholder="חפש קטגוריה..."
                                className="w-full pr-10 pl-4 py-2.5 rounded-lg border-2 border-line focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 outline-none transition-all text-sm text-ink-strong placeholder:text-ink-subtle bg-white hover:border-line-strong"
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto p-3 border-2 border-line rounded-xl bg-gradient-to-br from-surface-muted to-white scrollbar-thin scrollbar-thumb-neutral-300 scrollbar-track-neutral-100">
                            {filteredCategories.map((cat) => {
                                const totalSelected = newSkillData.categories.length + (customSkillCategory ? 1 : 0);
                                const isChecked = newSkillData.categories.includes(cat);
                                return (
                                    <motion.label
                                        key={cat}
                                        whileHover={{ scale: 1.02, x: 2 }}
                                        whileTap={{ scale: 0.98 }}
                                        className={`flex items-center gap-2.5 p-2.5 rounded-lg cursor-pointer transition-all border ${isChecked
                                            ? 'bg-gradient-to-br from-brand-300 to-brand-400 border-brand-500 text-white shadow-sm'
                                            : 'bg-white border-line hover:bg-gradient-to-br hover:from-brand-300/10 hover:to-brand-400/5 hover:border-brand-400/40'
                                            }`}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={isChecked}
                                            onChange={() => {
                                                if (isChecked) {
                                                    setNewSkillData({
                                                        ...newSkillData,
                                                        categories: newSkillData.categories.filter((c) => c !== cat)
                                                    });
                                                } else if (totalSelected < 3) {
                                                    setNewSkillData({
                                                        ...newSkillData,
                                                        categories: [...newSkillData.categories, cat]
                                                    });
                                                }
                                            }}
                                            className={`w-4 h-4 rounded ${isChecked ? 'accent-white' : 'accent-brand-500'}`}
                                        />
                                        <span className={`text-sm font-medium ${isChecked ? 'text-white' : 'text-ink-strong'}`}>{cat}</span>
                                    </motion.label>
                                );
                            })}
                        </div>

                        {/* Custom Category Input */}
                        <div className="mt-3">
                            <label className="block text-xs font-semibold text-ink-muted mb-2 flex items-center gap-1.5">
                                <div className="w-1 h-1 bg-secondary-500 rounded-full"></div>
                                או הוסף קטגוריה חדשה
                            </label>
                            <input
                                type="text"
                                value={customSkillCategory}
                                onChange={(e) => setCustomSkillCategory(e.target.value)}
                                placeholder="הקלד קטגוריה חדשה..."
                                disabled={newSkillData.categories.length >= 3}
                                className="w-full px-4 py-2.5 rounded-lg border-2 border-line focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 outline-none transition-all text-sm text-ink-strong placeholder:text-ink-subtle disabled:bg-surface-muted disabled:cursor-not-allowed hover:border-line-strong bg-white"
                            />
                            {customSkillCategory && allCategories.some(cat => cat.toLowerCase() === customSkillCategory.toLowerCase()) && (
                                <motion.p
                                    initial={{ opacity: 0, y: -5 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="text-xs text-danger-500 mt-2 flex items-center gap-1.5"
                                >
                                    <div className="w-1 h-1 bg-danger-500 rounded-full"></div>
                                    קטגוריה זו כבר קיימת - בחר אותה מהרשימה
                                </motion.p>
                            )}
                        </div>

                        {/* Category Counter */}
                        <motion.div
                            className="mt-3 flex items-center justify-center gap-2 px-3 py-2 bg-gradient-to-r from-surface-muted to-surface-muted rounded-lg border border-line"
                            animate={{ scale: [1, 1.02, 1] }}
                            transition={{ duration: 0.3 }}
                            key={newSkillData.categories.length}
                        >
                            <div className={`w-2 h-2 rounded-full ${newSkillData.categories.length >= 3 ? 'bg-success-500' : 'bg-warning-500'}`}></div>
                            <span className="text-xs font-semibold text-ink-strong">
                                {newSkillData.categories.length + (customSkillCategory && !allCategories.some(cat => cat.toLowerCase() === customSkillCategory.toLowerCase()) ? 1 : 0)} / 3 קטגוריות נבחרו
                            </span>
                        </motion.div>
                    </div>
                </div>

                {/* Fixed Bottom Actions */}
                <div className="p-6 border-t-2 border-line bg-gradient-to-br from-white to-surface-muted flex-shrink-0">
                    <div className="flex gap-3">
                        <motion.button
                            onClick={onClose}
                            whileHover={{ scale: 1.02, y: -1 }}
                            whileTap={{ scale: 0.98 }}
                            className="flex-1 px-6 py-3.5 rounded-xl border-2 border-line-strong text-ink-muted hover:bg-white hover:border-line-strong font-bold transition-all shadow-sm hover:shadow-md"
                        >
                            ביטול
                        </motion.button>
                        <motion.button
                            onClick={onSubmit}
                            whileHover={{ scale: 1.02, y: -1 }}
                            whileTap={{ scale: 0.98 }}
                            className="relative flex-1 px-6 py-3.5 rounded-xl bg-gradient-to-r from-brand-500 via-brand-600 to-brand-700 hover:from-brand-600 hover:via-brand-700 hover:to-[#5C6E54] text-white font-bold transition-all shadow-md hover:shadow-lg overflow-hidden group"
                        >
                            <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                            <span className="relative flex items-center justify-center gap-2">
                                {isEditMode ? (
                                    <>
                                        <Edit2 className="w-4 h-4" strokeWidth={2.5} />
                                        עדכן
                                    </>
                                ) : (
                                    <>
                                        <Plus className="w-4 h-4" strokeWidth={2.5} />
                                        הוסף
                                    </>
                                )}
                            </span>
                        </motion.button>
                    </div>
                </div>
            </motion.div>
        </motion.div>
    );
};
